/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { ReactElement, useContext, useEffect, useState } from 'react'

import { Button, Col, Form, Input, Row } from 'antd'
import { FormInstance } from 'antd/es/form/Form'
import { AxiosResponse } from 'axios'
import { SelectFieldAnt } from 'Components/Form'
import { SelectItemProps } from 'Components/Form/Select'
import ID_COMPONENTE from 'config/idComponente'
import classificacaoHorarios from 'Helpers/classificacaoHorarios'
import Claims from 'Hooks/usePermissions/Claims'
import usePermissions from 'Hooks/usePermissions/usePermissions'
import { ICandidato, IDoca, IHorario, IJanela } from 'Models/docas'
import IFormModoCompacto from 'Models/docas/IFormModoCompacto'
import { STATUS_HORARIOS } from 'Models/docas/IHorario'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'react-simple-snackbar'
import WsDocasService from 'Services/WS_DOCAS'
import { ServiceUpdateResponse } from 'Types/ServiceResponse'
import { MsgSuccessOptions } from 'Types/SnackbarTypes'

import { DocasContext } from './Context/context'

interface IProps {
  ativo: boolean
  atualizaAgenda: (
    limparSelecao?: boolean,
    executandoAcao?: boolean,
  ) => Promise<void>
}

const ModoCompacto = ({ ativo, atualizaAgenda }: IProps): ReactElement => {
  const hookForm = useForm()
  const [form] = Form.useForm()
  const { isAuthorized } = usePermissions()
  const { docasContext, setDocasContext } = useContext(DocasContext)
  const [carregandoAgendaSimplificada, setCarregandoAgendaSimplificada] =
    useState<boolean>(false)
  const [carregandoReservaHorario, setCarregandoReservaHorario] =
    useState<boolean>(false)
  const [selectJanelaItens, setSelectJanelaItens] = useState<SelectItemProps[]>(
    [],
  )
  const [selectDocaItens, setSelectDocaItens] = useState<SelectItemProps[]>([])
  const [selectHorarioItens, setSelectHorarioItens] = useState<
    SelectItemProps[]
  >([])
  const [horarios, setHorarios] = useState<IHorario[]>([])
  const [cdJanela, setCdJanela] = useState<number>()
  const [cdDoca, setCdDoca] = useState<number>()
  const [openSnackbarSuccess, closeSnackbarSuccess] =
    useSnackbar(MsgSuccessOptions)

  const [reservarAprovar, setReservarAprovar] = useState<boolean>(false)

  const getAgendaSimplificada = async () => {
    if (docasContext.armazemAtual) {
      setCarregandoAgendaSimplificada(true)

      const dataAtual: moment.Moment = moment()
      dataAtual.set('minute', 0)
      dataAtual.set('second', 0)
      dataAtual.set('millisecond', 0)

      const { data } = await WsDocasService.agenda.getAgendaSimplificada(
        ID_COMPONENTE.DOCAS,
        docasContext.armazemAtual.cdAgendamentoDocasArmazemConfig!,
        docasContext.dataAtual!.format('YYYY-MM-DD HH:mm:ss'),
      )

      if (data.item) {
        setSelectJanelaItens(
          data.item.janelas.map((janela: IJanela) => {
            return {
              value: janela.cdAgendamentoDocasJanela.toString(),
              label: janela.nomeJanela,
              disabled:
                classificacaoHorarios.buscaUltimoHorario(
                  janela.cdAgendamentoDocasJanela,
                  data.item!.horarios,
                ) < dataAtual,
            }
          }),
        )

        setSelectDocaItens(
          data.item.docas.map((doca: IDoca) => {
            return {
              value: doca.cdAgendamentoDocasDocas.toString(),
              label: doca.nomeDoca,
            }
          }),
        )

        setHorarios(data.item.horarios)
      }

      setCarregandoAgendaSimplificada(false)
    }
  }

  const reservaHorarios = async (dados: IFormModoCompacto) => {
    if (docasContext.candidatoSelecionado) {
      setCarregandoReservaHorario(true)

      const cdsHorarios = dados.cdsHorarios.map((cdHorario) =>
        window.parseInt(cdHorario),
      )

      const horariosSelecionados: IHorario[] = horarios.filter((horario) =>
        cdsHorarios.includes(horario.cdAgendamentoDocasHorario),
      )

      const tamanhoJanelaSecundariaMinutos: number =
        docasContext.armazemAtual?.tamanhoJanelaSecundaria ?? 60

      if (
        classificacaoHorarios.verificaHorariosConsecutivos(
          horariosSelecionados,
          tamanhoJanelaSecundariaMinutos,
        )
      ) {
        const respostaAcao: AxiosResponse<ServiceUpdateResponse> =
          await WsDocasService.agenda.executaAcao(
            ID_COMPONENTE.DOCAS,
            cdsHorarios.map((cdHorario) => {
              return {
                cdAgendamentoDocasHorario: cdHorario,
                horarioStatus: reservarAprovar
                  ? STATUS_HORARIOS.RESERVADO_APROVADO
                  : STATUS_HORARIOS.RESERVADO,
                cdCandidatoAgendamento:
                  docasContext.candidatoSelecionado!.cdCandidatoAgendamento,
                agendamentoMultiplo: dados.cdsHorarios.length > 1,
                placaVeiculo: docasContext.candidatoSelecionado!.placa,
                transportadora:
                  docasContext.candidatoSelecionado!.nomeTransportadora,
                cdEntidade: docasContext.candidatoSelecionado!.cdEntidade,
                cdVeiculo: docasContext.candidatoSelecionado!.cdVeiculo,
                cdViagem: docasContext.candidatoSelecionado!.cdViagem,
                observacao: dados.observacao,
              }
            }),
          )

        if (respostaAcao.data.statusCode == 501 /*Sucesso*/) {
          await getAgendaSimplificada()
          await atualizaAgenda()

          form.resetFields()
          setCdJanela(undefined)
          setCdDoca(undefined)
        } else {
          const mensagem: string =
            respostaAcao.data.message ??
            'Ocorreu um erro ao reservar o(s) horário(s), tente novamente'
          openSnackbarSuccess(mensagem)
        }
      } else {
        openSnackbarSuccess(
          'Não é possível fazer uma reserva em horários não consecutivos',
        )
      }

      setCarregandoReservaHorario(false)
    } else {
      openSnackbarSuccess('Selecione um candidato para reservar')
    }
  }

  useEffect(() => {
    if (selectJanelaItens.length > 1) {
      setCdJanela(undefined)
      form.resetFields()
    }
  }, [docasContext.armazemAtual?.cdAgendamentoDocasArmazem, selectJanelaItens])

  useEffect(() => {
    if (ativo) {
      getAgendaSimplificada()
    }
  }, [docasContext.armazemAtual, docasContext.dataAtual, ativo])

  useEffect(() => {
    const dataAtual: moment.Moment = moment()
    dataAtual.set('minute', 0)
    dataAtual.set('second', 0)
    dataAtual.set('millisecond', 0)

    setSelectHorarioItens(
      horarios
        .filter(
          (horario) =>
            horario.cdAgendamentoDocasJanela === cdJanela &&
            horario.cdAgendamendoDocasDocas === cdDoca,
        )
        .map((horario: IHorario) => {
          return {
            value: horario.cdAgendamentoDocasHorario.toString(),
            label: horario.hora,
            disabled: moment(horario.dtHora, 'YYYY-MM-DDTHH:mm:ss') < dataAtual,
          }
        }),
    )
  }, [cdJanela, cdDoca])

  useEffect(() => {
    if (!cdJanela && selectJanelaItens.length === 1) {
      const cdJanela: string = selectJanelaItens[0].value
      form.setFieldsValue({ cdJanela })
      setCdJanela(window.parseInt(cdJanela))
    }
  }, [selectJanelaItens])

  return isAuthorized(Claims.ReservarHorario) ? (
    <Form
      form={form}
      onFinish={reservaHorarios}
      layout="vertical"
      style={{ width: '100%' }}
    >
      <Row gutter={16}>
        <Col span={5}>
          <SelectFieldAnt
            methods={hookForm}
            scope="form-modo-compacto"
            label="Janela:"
            name="cdJanela"
            items={selectJanelaItens}
            onChangeValue={(cdJanela: string) =>
              setCdJanela(window.parseInt(cdJanela))
            }
            showSearch={true}
            disabled={carregandoAgendaSimplificada}
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          />
        </Col>

        <Col span={5}>
          <SelectFieldAnt
            methods={hookForm}
            scope="form-modo-compacto"
            label="Doca:"
            name="cdDoca"
            items={selectDocaItens}
            onChangeValue={(cdDoca: string) =>
              setCdDoca(window.parseInt(cdDoca))
            }
            showSearch={true}
            disabled={carregandoAgendaSimplificada}
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          />
        </Col>

        <Col span={5}>
          <SelectFieldAnt
            methods={hookForm}
            scope="form-modo-compacto"
            label="Horário(s):"
            name="cdsHorarios"
            items={selectHorarioItens}
            mode="multiple"
            showSearch={true}
            disabled={!cdJanela || !cdDoca}
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          />
        </Col>

        <Col span={5}>
          <Form.Item name="observacao" label="Observação Transportadora:">
            <Input type="text" />
          </Form.Item>
        </Col>

        {/* <Col span={3}>
          <Button
            type="primary"
            htmlType="submit"
            loading={carregandoReservaHorario}
            style={{ marginTop: '30.5px' }}
          >
            Reservar
          </Button>
        </Col> */}

        {isAuthorized(Claims.ReservarAprovarHorario) && (
          <Col span={3}>
            <Button
              type="primary"
              htmlType="submit"
              loading={carregandoReservaHorario}
              style={{ marginTop: '30.5px' }}
              onClick={() => setReservarAprovar(true)}
            >
              Reservar e Aprovar
            </Button>
          </Col>
        )}
      </Row>
    </Form>
  ) : (
    <div style={{ color: 'red', marginBottom: '10px' }}>
      Você não tem permissão para reservar horários
    </div>
  )
}

export default ModoCompacto
